// ThemeContext: Provides dark/light mode and persists state
import React, { createContext, useState, useMemo, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

export const ThemeContext = createContext();

const getInitialMode = () => {
  const saved = localStorage.getItem('themeMode');
  return saved ? saved : 'light';
};

export function CustomThemeProvider({ children }) {
  const [mode, setMode] = useState(getInitialMode());

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const theme = useMemo(() =>
    createTheme({
      palette: {
        mode,
        primary: { main: '#00a679' },
        secondary: { main: '#ec2127' },
        ...(mode === 'light' ? {
          background: {
            default: '#f5f6fa',
            paper: '#fff',
          },
          text: {
            primary: '#222',
            secondary: '#555',
          },
        } : {
          background: {
            default: '#181a20',
            paper: '#23272f',
          },
          text: {
            primary: '#fff',
            secondary: '#bbb',
          },
        })
      },
      typography: {
        fontFamily: 'Inter, Roboto, Arial, sans-serif',
        fontWeightRegular: 400,
        fontWeightBold: 700,
        h1: { color: '#00a679', fontWeight: 700 },
        h2: { color: '#00a679', fontWeight: 700 },
        h3: { color: '#00a679', fontWeight: 700 },
        h4: { color: '#00a679', fontWeight: 700 },
        h5: { color: '#00a679', fontWeight: 700 },
        h6: { color: '#00a679', fontWeight: 700 },
        ...(mode === 'light' ? {
          allVariants: {
            color: '#222',
          },
        } : {
          allVariants: {
            color: '#fff',
          },
        })
      },
      components: {
        MuiAppBar: {
          styleOverrides: {
            colorInherit: {
              backgroundColor: mode === 'light' ? '#fff' : '#23272f',
              color: mode === 'light' ? '#222' : '#fff',
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: mode === 'light' ? '#fff' : '#23272f',
              color: mode === 'light' ? '#222' : '#fff',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              fontWeight: 700,
              borderRadius: 8,
              textTransform: 'none',
            },
          },
        },
      },
    }), [mode]);

  const toggleMode = () => setMode((prev) => (prev === 'light' ? 'dark' : 'light'));

  return (
    <ThemeContext.Provider value={{ mode, toggleMode }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
}
